// ============================================================================
// EHR INTEGRATION SETTINGS VIEW — SMART Build 2P (dedicated page)
//
// Standalone admin/integration surface for the tenant SMART/FHIR connection.
// Moved out of Impact Settings (which had grown cluttered). Renders the existing
// EhrIntegrationCard UNCHANGED — Connection Settings, EHR User Mappings, SMART
// Sandbox Test Console, and IT Setup Guide all still live inside that card.
//
// Access (identical to the previous in-Impact-Settings placement; also enforced
// by App RouteGuard + rbac getAllowedRoutes):
//   - platform_admin: tenant picker; full edit (canEdit=true)
//   - tenant_admin:   own tenant only; read-only (canEdit=false)
//   - other roles:    no access
//
// No EHR business logic, SMART launch/callback/token, Patient/Coverage import,
// Gate, Packet Inbox, OCR, or Firestore behavior is changed here.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Plug, Building, ChevronDown, Loader2, AlertCircle, Shield } from 'lucide-react';
import { getTenants, isPlatformAdmin, isTenantAdmin } from '../core/index.js';
import { useAuth } from '../auth/AuthContext.jsx';
import EhrIntegrationCard from './EhrIntegrationCard.jsx';

const EhrIntegrationSettingsView = () => {
  const { user } = useAuth();
  const userIsPlatformAdmin = isPlatformAdmin(user?.role);
  const userIsTenantAdmin = isTenantAdmin(user?.role);
  const showTenantPicker = userIsPlatformAdmin;

  const [tenants, setTenants] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Same tenant-resolution behavior as TenantImpactSettingsView: platform_admin
  // picks a tenant; tenant_admin is pinned to their own tenant.
  useEffect(() => {
    if (showTenantPicker) {
      getTenants()
        .then((data) => setTenants(data || {}))
        .catch((err) => { console.error('Error loading tenants:', err); setError('Failed to load tenants'); })
        .finally(() => setLoading(false));
    } else if (user?.tenantId) {
      setSelectedTenantId(user.tenantId);
      setLoading(false);
    } else {
      setError('No tenant assigned to your account');
      setLoading(false);
    }
  }, [showTenantPicker, user?.tenantId]);

  // Defense in depth — App RBAC already gates this route to admins.
  if (!userIsPlatformAdmin && !userIsTenantAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-red-400">Access Denied</h3>
            <p className="text-sm text-red-300/80 mt-1">
              You do not have permission to view EHR Integration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center">
            <Plug className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">EHR Integration</h1>
            <p className="text-sm text-slate-400">
              Configure SMART-on-FHIR connections, sandbox testing, user mapping, and hospital IT setup.
            </p>
          </div>
        </div>
        {userIsTenantAdmin && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg w-fit">
            <Shield className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-amber-400 font-medium">Read-only — Managed by Platform Admin</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Tenant Selector (platform_admin) or Tenant Label (tenant_admin) */}
          {showTenantPicker ? (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">Select Tenant</label>
              <div className="relative">
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-sky-500/50"
                >
                  {Object.keys(tenants).length === 0 ? (
                    <option value="">No tenants available</option>
                  ) : (
                    <>
                      <option value="">Select a tenant...</option>
                      {Object.values(tenants).map((tenant) => (
                        <option key={tenant.id || tenant.tenantId} value={tenant.id || tenant.tenantId}>
                          {tenant.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>
          ) : (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Building className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm font-medium text-slate-300">Your Organization</p>
                <p className="text-xs text-slate-500">Tenant ID: {selectedTenantId}</p>
              </div>
            </div>
          )}

          {/* EHR Integration card — internals unchanged (platform_admin edit; tenant_admin read-only). */}
          {selectedTenantId ? (
            <EhrIntegrationCard tenantId={selectedTenantId} canEdit={userIsPlatformAdmin} />
          ) : (
            <div className="text-center py-16 text-slate-400">
              Select a tenant to configure EHR integration
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EhrIntegrationSettingsView;
